import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * Resolves the current tenant from the URL. For `/shop/{slug}` we look up the
 * owner via `GET /api/store/resolve-slug?slug=...` and cache the owner_id in
 * localStorage so subsequent pages (cart, checkout, signup) also target the
 * right tenant even when the slug drops from the URL (e.g. `/shop/checkout`).
 *
 * For plain `/shop` we let the backend fall back to the default tenant, which
 * keeps single-tenant installs working without any config.
 *
 * Also loads the brand doc for the resolved tenant and applies the shopper-
 * visible theme (colours, font, hero) as CSS variables + a Google Fonts link
 * so each tenant's storefront actually looks like *their* brand.
 */

const STORAGE_KEY = "dialflow_shop_tenant_v1";
const TenantContext = createContext(null);

// Google Fonts stylesheets keyed by the id we save in brand.font_family.
// Only load a stylesheet when the tenant explicitly picks that font — we
// don't want to slow down every storefront with fonts nobody uses.
const FONT_LINKS = {
  inter: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap",
  manrope: "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&display=swap",
  space: "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap",
  dm: "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap",
  poppins: "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap",
  playfair: "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&display=swap",
};
const FONT_STACKS = {
  inter: "'Inter', system-ui, sans-serif",
  manrope: "'Manrope', system-ui, sans-serif",
  space: "'Space Grotesk', system-ui, sans-serif",
  dm: "'DM Sans', system-ui, sans-serif",
  poppins: "'Poppins', system-ui, sans-serif",
  playfair: "'Playfair Display', Georgia, serif",
};

function applyBrandTheme(brand) {
  const root = document.documentElement;
  if (brand?.primary_color) root.style.setProperty("--shop-primary", brand.primary_color);
  if (brand?.accent_color) root.style.setProperty("--shop-accent", brand.accent_color);
  const font = brand?.font_family;
  if (font && FONT_STACKS[font]) {
    root.style.setProperty("--shop-font", FONT_STACKS[font]);
    // Idempotent link injection — one <link> per font family.
    const linkId = `shop-font-${font}`;
    if (!document.getElementById(linkId) && FONT_LINKS[font]) {
      const l = document.createElement("link");
      l.id = linkId;
      l.rel = "stylesheet";
      l.href = FONT_LINKS[font];
      document.head.appendChild(l);
    }
  } else {
    root.style.removeProperty("--shop-font");
  }
  // Theme mode → a class on <body> so any storefront CSS can react.
  document.body.classList.remove("shop-theme-light", "shop-theme-dark", "shop-theme-auto");
  const mode = brand?.theme_mode || "light";
  document.body.classList.add(`shop-theme-${mode}`);
}

function clearBrandTheme() {
  const root = document.documentElement;
  root.style.removeProperty("--shop-primary");
  root.style.removeProperty("--shop-accent");
  root.style.removeProperty("--shop-font");
  document.body.classList.remove("shop-theme-light", "shop-theme-dark", "shop-theme-auto");
}

// Swap the site-wide <link rel="manifest"> and <meta theme-color> so the
// browser's "Install this app" prompt (and iOS Add-to-Home) uses the
// current tenant's shop name, colours and icons instead of the generic
// DialFlow manifest. Idempotent — safe to call on every render.
async function applyManifest({ slug, ownerId, brand }) {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const qs = slug ? `slug=${encodeURIComponent(slug)}` : (ownerId ? `owner_id=${encodeURIComponent(ownerId)}` : "");
  const dynamicHref = qs ? `${backend}/api/store/manifest.json?${qs}` : `${backend}/api/store/manifest.json`;
  // Fallback when the backend is unreachable — kept per-slug (not the
  // generic /shop-manifest.json) so a flaky backend can never regress the
  // install destination back to the wrong shop; see index.js's synchronous
  // pre-mount manifest pin for why start_url correctness can't wait on this
  // fetch at all when it's avoidable.
  // Every URL below must be absolute — a manifest served from a blob: URL
  // has no base to resolve relative references against, so a relative
  // start_url/scope/icon src gets silently rejected by Chrome ("URL is
  // invalid") and the whole fallback manifest is ignored.
  const origin = window.location.origin;
  const fallbackHref = slug
    ? URL.createObjectURL(
        new Blob(
          [
            JSON.stringify({
              name: "DialFlow Shop",
              short_name: "Shop",
              start_url: `${origin}/shop/${slug}`,
              scope: `${origin}/shop/${slug}`,
              display: "standalone",
              orientation: "portrait",
              background_color: "#ffffff",
              theme_color: "#0055FF",
              icons: [
                { src: `${origin}/icons/icon-192.png`, sizes: "192x192", type: "image/png", purpose: "any" },
                { src: `${origin}/icons/icon-512.png`, sizes: "512x512", type: "image/png", purpose: "any" },
                { src: `${origin}/icons/icon-maskable.png`, sizes: "512x512", type: "image/png", purpose: "maskable" },
              ],
            }),
          ],
          { type: "application/json" },
        ),
      )
    : "/shop-manifest.json";

  // Probe the dynamic endpoint first (2s timeout). If Railway/backend is
  // down or slow, gracefully fall through to the fallback manifest so the
  // PWA install prompt still works and still points at the right shop.
  let href = fallbackHref;
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 2000);
    const res = await fetch(dynamicHref, { signal: ctrl.signal, credentials: "omit" });
    clearTimeout(t);
    if (res.ok) href = dynamicHref;
  } catch {
    // Network error / abort — fallback manifest keeps PWA install working.
  }

  let link = document.querySelector('link[rel="manifest"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "manifest";
    document.head.appendChild(link);
  }
  if (link.getAttribute("href") !== href) link.setAttribute("href", href);

  // Match the address-bar / status-bar colour to the shop's brand.
  const themeMeta = document.querySelector('meta[name="theme-color"]');
  if (themeMeta && brand?.primary_color) themeMeta.setAttribute("content", brand.primary_color);

  // iOS Safari uses THIS title (not the manifest name) for the home-screen icon.
  const iosTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
  if (iosTitle && brand?.shop_name) iosTitle.setAttribute("content", brand.shop_name);
}

function restoreDefaultManifest() {
  // Revert to the static owner/employee manifest on unmount so the owner
  // console doesn't try to install as "computer care" on iPad, etc.
  const link = document.querySelector('link[rel="manifest"]');
  if (link) link.setAttribute("href", "/manifest.json");
  const themeMeta = document.querySelector('meta[name="theme-color"]');
  if (themeMeta) themeMeta.setAttribute("content", "#0055FF");
  const iosTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
  if (iosTitle) iosTitle.setAttribute("content", "DialFlow");
}

export function TenantProvider({ children }) {
  const { slug } = useParams();
  const [ownerId, setOwnerId] = useState(null);
  const [shopName, setShopName] = useState("");
  const [resolvedSlug, setResolvedSlug] = useState(null);
  const [brand, setBrand] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | notfound
  const [healthcareEnabled, setHealthcareEnabled] = useState(false);
  const [hasCatalog, setHasCatalog] = useState(null); // null = unknown, else boolean

  const persist = useCallback((data) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch { /* ignore quota errors */ }
  }, []);

  useEffect(() => {
    // Hydrate from localStorage on first paint so a hard-reload of
    // /shop/checkout still targets the right tenant.
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw);
        setOwnerId(data.owner_id || null);
        setShopName(data.shop_name || "");
        setResolvedSlug(data.slug || null);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!slug) {
      setStatus("ready");
      return;
    }
    setStatus("loading");
    api.get(`/store/resolve-slug`, { params: { slug } })
      .then(({ data }) => {
        setOwnerId(data.owner_id);
        setShopName(data.shop_name || "");
        setResolvedSlug(data.slug || slug);
        setHealthcareEnabled(!!data.healthcare_module_enabled);
        persist({ owner_id: data.owner_id, shop_name: data.shop_name, slug: data.slug });
        setStatus("ready");
      })
      .catch(() => setStatus("notfound"));
  }, [slug, persist]);

  // Whether this tenant has any product catalog at all — lets /care offer a
  // "browse products" cross-link only when there's something to browse.
  useEffect(() => {
    if (!ownerId) return undefined;
    let cancelled = false;
    api.get("/store/catalog", { params: { owner_id: ownerId } })
      .then(({ data }) => !cancelled && setHasCatalog(Array.isArray(data) && data.length > 0))
      .catch(() => !cancelled && setHasCatalog(false));
    return () => { cancelled = true; };
  }, [ownerId]);

  // Fetch + apply the brand theme whenever the tenant changes.
  useEffect(() => {
    let cancelled = false;
    if (!ownerId && !resolvedSlug) return undefined;
    api.get("/store/brand", {
      params: resolvedSlug ? { slug: resolvedSlug } : { owner_id: ownerId },
    })
      .then(({ data }) => {
        if (cancelled) return;
        setBrand(data || null);
        applyBrandTheme(data || null);
        // Swap the PWA manifest so the "Install app" prompt shows THIS
        // shop's name + colours (per-tenant PWA install).
        applyManifest({ slug: resolvedSlug, ownerId, brand: data || null });
      })
      .catch(() => { /* fall back to defaults */ });
    return () => { cancelled = true; };
  }, [ownerId, resolvedSlug]);

  // On unmount (e.g. shopper leaves the storefront section) clear the theme
  // so the owner console doesn't inherit a stray tenant colour.
  useEffect(() => () => {
    clearBrandTheme();
    restoreDefaultManifest();
  }, []);

  const value = useMemo(
    () => ({ ownerId, shopName, slug: resolvedSlug, status, brand, healthcareEnabled, hasCatalog }),
    [ownerId, shopName, resolvedSlug, status, brand, healthcareEnabled, hasCatalog],
  );
  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) return { ownerId: null, shopName: "", slug: null, status: "ready", brand: null, healthcareEnabled: false, hasCatalog: null };
  return ctx;
}
